import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function Login() {
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState("");

  React.useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("auth_error");
    if (!code) return;
    setNotice(
      code === "domain"
        ? "That Google account isn't a @williamsdiversified.com address. Ironclad is restricted to Williams Diversified Google accounts — sign in with your WD account."
        : "Google sign-in didn't complete. Please try again."
    );
    // clean the query string so the notice doesn't persist on refresh
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const handleLogin = async () => {
    setBusy(true);
    try {
      // OWN Google OAuth 2.0 client — goes STRAIGHT to Google's account chooser (no interstitial).
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const { data } = await api.get("/auth/google/config");
      if (data?.configured && data?.client_id) {
        const origin = window.location.origin;
        const redirectUri = origin + "/api/auth/google/callback";
        const params = new URLSearchParams({
          client_id: data.client_id,
          redirect_uri: redirectUri,
          response_type: "code",
          scope: "openid email profile",
          prompt: "select_account",
          access_type: "online",
          state: origin,
        });
        if (data.hd) params.set("hd", data.hd);
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
        return;
      }
      // Fallback: platform-managed auth until our own OAuth client credentials are configured.
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/dashboard")}`;
    } catch {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/dashboard")}`;
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-zinc-950">
      {/* Left: brand */}
      <div className="flex flex-col justify-between p-10 lg:p-14 text-zinc-100">
        <div className="flex items-center gap-4">
          <img
            src="/wd_brand_logo.png"
            alt="Williams Diversified"
            data-testid="login-brand-logo"
            className="h-28 w-28 lg:h-36 lg:w-36 object-contain shrink-0"
          />
          <div className="leading-tight">
            <div className="font-display font-bold text-4xl lg:text-5xl tracking-tight text-white">Ironclad</div>
            <div className="text-sm lg:text-base tracking-[0.14em] text-zinc-300 mt-1">by Williams Diversified</div>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            Every document. Every deal.
          </h1>
        </div>

        <p className="text-[11px] text-zinc-600 font-mono">Ironclad by Williams Diversified</p>
      </div>

      {/* Right: sign-in card over architecture image */}
      <div
        className="relative flex items-center justify-center p-10 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1527576539890-dfa815648363?auto=format&fit=crop&w=1400&q=70')" }}
      >
        <div className="absolute inset-0 bg-zinc-950/70" />
        <div className="relative w-full max-w-sm bg-white border border-zinc-200 rounded-sm p-8 fade-in">
          <div className="text-[10px] tracking-[0.2em] text-zinc-500 font-semibold">Secure access</div>
          <h2 className="font-display font-bold text-2xl tracking-tight mt-2 text-zinc-900">Sign in</h2>
          <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
            For bid writers and administrators. Authenticate with your Williams Diversified Google account.
          </p>
          {notice && (
            <div
              data-testid="login-auth-error"
              className="mt-4 flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{notice}</span>
            </div>
          )}
          <Button
            data-testid="google-login-button"
            onClick={handleLogin}
            disabled={busy}
            className="w-full mt-6 h-11 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm font-medium group"
          >
            {busy ? "Redirecting…" : "Continue with Google"}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
          <p className="text-[11px] text-zinc-400 mt-4 font-mono">
            Sessions expire after 7 days. All activity is logged to the audit trail.
          </p>
        </div>
      </div>
    </div>
  );
}
